// File: frontend/src/components/marketing/PaymentFlowSection.js
//
// One purchase, told as five steps beside the customer's phone: choose a
// package → pay with MTN or Airtel → payment confirmed → internet on → the
// money lands in the operator's wallet. Same hotspot, same package, same price
// and same sale throughout.
//
// ── SCROLL DRIVES IT. NOTHING PLAYS BY ITSELF ───────────────────────────────
//
// This used to be a timeline: on entering the viewport it played the five steps
// about a second apart, whatever the reader was doing. A reader who stopped
// scrolling still lost the step they were reading, and a reader who scrolled
// back got nothing. It is now the same model as the product tour — the section
// is several viewports tall, its content is CSS-sticky under the header, and a
// ScrollTrigger maps scroll progress onto the active step. Nothing is pinned,
// no wheel or touch event is intercepted, stopping holds the step, scrolling
// back reverses it, and scrolling past releases the section.
//
// Choosing a step still works, and switches at once: it scrolls to that step's
// slot so position and selection agree afterwards, and scroll updates are
// ignored until that scroll is done, or every step in between would flash past.
//
// ── PHONES GET THE STEPPER, NOT THE STORY ───────────────────────────────────
//
// A sticky story needs its whole content inside one viewport. On a 390x844
// phone the heading, the active step, the phone and the controls come to about
// 730px against roughly 680px of usable `svh` once the browser toolbar is
// showing — it does not fit, and forcing it would clip the phone. So phones get
// the active step's heading and explanation above the phone, both changing
// together, with numbered controls to move between them. The text sits in a
// reserved block so the phone never moves as the wording changes.
//
// ── TWO FRAMES ON PURPOSE ───────────────────────────────────────────────────
//
// Desktop shows the real photographed phone (visuals/FeatureVisuals PhoneStage)
// with the portal drawn as HTML on its screen. The screen is only ~45% of that
// photograph's width, so below md — where the whole stage would be under 360px
// wide and the screen under 165px — the interface would be too small to read,
// and phones use the drawn frame instead. Within each breakpoint it is one
// frame that stays put while its screen changes, which is the point.
//
// The static page shows the finished story, which is also what reduced motion
// and no-JavaScript visitors get.

import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Container, Stack, Typography, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { MarketingMobileFrame } from "./visuals/primitives";
import { PhoneStage } from "./visuals/FeatureVisuals";
import {
  PlansScreen, PayScreen, ConfirmedScreen, OnlineScreen, WalletEntryCard,
} from "./visuals/PaymentPhoneScreens";
import {
  gsap, useGSAP, ScrollTrigger, MQ, STORY_MQ, HEADER_H, isBelowFold, prefersReducedMotion, useReveal,
} from "./landingMotion";
import { COLLECTION_FEE_TOTAL_PCT } from "../../content/publicPricing";

const STEPS = [
  { title: "Choose a package", text: "Your customer joins your Wi-Fi and picks a plan on your login page." },
  { title: "Pay with Mobile Money", text: "They enter an MTN or Airtel number and approve the prompt on their own phone. The PIN never touches PesaFii." },
  { title: "Payment confirmed", text: "PesaFii confirms the payment with the provider and records the sale." },
  { title: "Internet switches on", text: "Their device is connected for the time or data they bought. Nobody has to tap connect." },
  { title: "Money in your wallet", text: `The sale lands in your wallet, less PesaFii's ${COLLECTION_FEE_TOTAL_PCT}% fee, ready to withdraw to Mobile Money.` },
];
const SCREENS = [PlansScreen, PayScreen, ConfirmedScreen, OnlineScreen];
const FINAL = STEPS.length - 1;
const screenFor = (s) => Math.min(s, SCREENS.length - 1);

// Also used as a React style prop, which React freezes: GSAP always gets a
// copy ({ ...HIDDEN }), because gsap.set writes into the object it is given.
const HIDDEN = { visibility: "hidden", opacity: 0 };
const initialStyle = (i) => (i === screenFor(FINAL) ? undefined : { ...HIDDEN });

// Scroll distance per step in story mode.
const PER_STEP = "38svh";
const CLICK_LOCK_MS = 1200;

export default function PaymentFlowSection() {
  const [step, setStep] = useState(FINAL);
  const root = useReveal();
  const shownScreen = useRef(screenFor(FINAL));
  const walletShown = useRef(true);
  const story = useRef(null);
  const lockUntil = useRef(0);

  const { contextSafe } = useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ story: STORY_MQ, always: MQ.always }, (ctx) => {
        // The tall layout is CSS behind html.js; without it there is no story.
        if (!ctx.conditions.story || !document.documentElement.classList.contains("js")) return undefined;
        const el = root.current;
        // Rewind to step 1 only for a story the reader has not reached yet. On a
        // reload part-way down the page the section is already on screen, and
        // resetting it would play a transition in front of someone who is
        // reading it; ScrollTrigger corrects the step on its first update
        // anyway.
        if (isBelowFold(el, 0.9)) setStep(0);
        const start = `top top+=${HEADER_H}`;
        story.current = ScrollTrigger.create({
          trigger: el,
          start,
          end: "bottom bottom",
          onUpdate: (self) => {
            if (Date.now() < lockUntil.current) return;
            const i = Math.min(FINAL, Math.floor(self.progress * STEPS.length));
            setStep((prev) => (prev === i ? prev : i));
          },
        });
        return () => { story.current = null; };
      });
    },
    { scope: root, dependencies: [] }
  );

  const render = contextSafe((s) => {
    const q = gsap.utils.selector(root);
    const reduce = prefersReducedMotion();
    const screen = screenFor(s);

    if (screen !== shownScreen.current) {
      const layer = (i) => q(`[data-screen="${i}"]`);
      const all = SCREENS.map((_, i) => layer(i)).flat();
      gsap.killTweensOf(all);
      gsap.set(SCREENS.map((_, i) => i).filter((i) => i !== screen && i !== shownScreen.current).flatMap(layer), { ...HIDDEN });
      if (reduce) {
        gsap.set(layer(shownScreen.current), { ...HIDDEN });
        gsap.set(layer(screen), { visibility: "visible", opacity: 1, x: 0 });
      } else {
        const dir = screen > shownScreen.current ? 1 : -1;
        gsap.to(layer(shownScreen.current), { autoAlpha: 0, x: -24 * dir, duration: 0.28, ease: "power2.in" });
        gsap.fromTo(layer(screen), { autoAlpha: 0, x: 24 * dir }, { autoAlpha: 1, x: 0, duration: 0.45, delay: 0.12, ease: "power3.out" });
      }
      shownScreen.current = screen;
    }

    const wantWallet = s === FINAL;
    if (wantWallet !== walletShown.current) {
      const wallet = q("[data-wallet]");
      gsap.killTweensOf(wallet);
      if (reduce) gsap.set(wallet, wantWallet ? { visibility: "visible", opacity: 1, y: 0, scale: 1 } : { ...HIDDEN });
      else if (wantWallet) gsap.fromTo(wallet, { autoAlpha: 0, y: 18, scale: 0.94 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.5)" });
      else gsap.to(wallet, { autoAlpha: 0, y: 10, duration: 0.2 });
      walletShown.current = wantWallet;
    }

    gsap.to(q("[data-line-fill]"), { scaleY: s / FINAL, duration: reduce ? 0 : 0.6, ease: "power2.inOut" });
  });

  useEffect(() => { render(step); }, [step, render]);

  const choose = (i) => {
    setStep(i);
    const st = story.current;
    if (!st) return;
    const top = st.start + (st.end - st.start) * ((i + 0.5) / STEPS.length);
    lockUntil.current = Date.now() + CLICK_LOCK_MS;
    window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  const storyCss = `@media ${STORY_MQ}`;

  // The four customer screens, shared by both frames.
  const screens = SCREENS.map((Screen, i) => (
    <Box key={i} data-screen={i} style={initialStyle(i)} sx={{ minWidth: 0, height: "100%" }}>
      <Screen />
    </Box>
  ));

  return (
    <Box
      component="section"
      id="payments"
      ref={root}
      aria-labelledby="payments-title"
      sx={(t) => ({
        scrollMarginTop: HEADER_H,
        position: "relative",
        backgroundImage: `radial-gradient(ellipse 45% 55% at 78% 50%, ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.1 : 0.07)}, transparent 70%)`,
        [storyCss]: {
          "html.js &": { height: `calc(100svh - ${HEADER_H}px + ${STEPS.length - 1} * ${PER_STEP})` },
          "html.js & [data-story-sticky]": {
            position: "sticky", top: HEADER_H, height: `calc(100svh - ${HEADER_H}px)`,
            display: "flex", alignItems: "center", py: 0,
          },
        },
      })}
    >
      <Box data-story-sticky="" sx={{ py: { xs: 7, md: 9 }, width: "100%" }}>
        <Container maxWidth="lg" sx={{ minWidth: 0 }}>
          <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: { xs: 3, md: 4 } }}>
            <Typography data-reveal="" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mb: 1 }}>
              Payments
            </Typography>
            {/* This used to read "From Payment to Internet Access in Seconds".
                Measured over 40 real purchases, buy-to-online is about 23
                seconds — but 13.6s of that is the customer typing their own PIN
                at the provider, which is not ours to claim. What IS seconds is
                everything after the payment is confirmed. */}
            <Typography data-reveal="" id="payments-title" component="h2" sx={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", lineHeight: 1.18, fontWeight: 800, letterSpacing: "-0.02em", color: "text.primary" }}>
              Paid with Mobile Money. Online seconds later.
            </Typography>
            <Stack data-reveal="" direction="row" justifyContent="center" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
              {["MTN Mobile Money", "Airtel Money"].map((n) => (
                <Box key={n} component="span" sx={{ px: 1.5, py: 0.5, borderRadius: 99, fontSize: 13, fontWeight: 700, color: "text.primary", border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  {n}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* ── Phones: the active step above the phone ───────────────────── */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {/* Reserved height, so changing step never moves the phone. */}
            <Box sx={{ minHeight: 132, mb: 2 }}>
              <Typography component="h3" sx={{ fontSize: 19, fontWeight: 800, color: "text.primary", mb: 0.75 }}>
                <Box component="span" sx={{ color: "primary.main" }}>{step + 1}.</Box> {STEPS[step].title}
              </Typography>
              <Typography sx={{ fontSize: 14.5, lineHeight: 1.6, color: "text.secondary" }}>
                {STEPS[step].text}
              </Typography>
            </Box>

            <Box aria-hidden sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <MarketingMobileFrame sx={{ width: 264 }}>
                <Box sx={{ display: "grid", height: 440, "& > *": { gridArea: "1 / 1" } }}>{screens}</Box>
              </MarketingMobileFrame>
              <Box data-wallet="" sx={{ mt: 2.5 }}>
                <WalletEntryCard />
              </Box>
            </Box>

            <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 3 }}>
              {STEPS.map((s, i) => (
                <ButtonBase
                  key={s.title}
                  onClick={() => setStep(i)}
                  aria-label={`Step ${i + 1}: ${s.title}`}
                  aria-current={i === step ? "step" : undefined}
                  sx={(t) => ({
                    width: 38, height: 38, borderRadius: "50%", fontWeight: 800, fontSize: 14,
                    border: "2px solid",
                    borderColor: i <= step ? "primary.main" : "divider",
                    bgcolor: i === step ? "primary.main" : "transparent",
                    color: i === step ? "primary.contrastText" : i < step ? "primary.main" : "text.secondary",
                    "&.Mui-focusVisible": { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                  })}
                >
                  {i < step ? <CheckIcon sx={{ fontSize: 17 }} /> : i + 1}
                </ButtonBase>
              ))}
            </Stack>

            {/* The static page carries every step, so a crawler and a
                JavaScript-off reader get the whole story. */}
            <Box component="ol" sx={{ listStyle: "none", m: 0, mt: 3, p: 0, "html.js &": { display: "none" } }}>
              {STEPS.filter((_, i) => i !== step).map((s, i) => (
                <Box component="li" key={s.title} sx={{ mb: 2 }}>
                  <Typography component="h3" sx={{ fontSize: 17, fontWeight: 800, color: "text.primary" }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: 14.5, color: "text.secondary" }}>{s.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Desktop: steps beside the photographed phone ──────────────── */}
          <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "1fr 1fr", gap: 6, alignItems: "center" }}>
            <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, position: "relative", minWidth: 0 }}>
              <Box aria-hidden sx={(t) => ({ position: "absolute", left: 19, top: 24, bottom: 24, width: 2, borderRadius: 99, bgcolor: alpha(t.palette.text.primary, 0.1), overflow: "hidden" })}>
                <Box data-line-fill="" sx={{ height: "100%", bgcolor: "primary.main", transformOrigin: "top center" }} />
              </Box>
              {STEPS.map((s, i) => {
                const done = i < step;
                const current = i === step;
                return (
                  <Box component="li" key={s.title} sx={{ position: "relative" }}>
                    <ButtonBase
                      onClick={() => choose(i)}
                      aria-current={current ? "step" : undefined}
                      // justifyContent: ButtonBase centres its content, which would
                      // indent every row by its own width.
                      sx={{ width: "100%", display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 2, textAlign: "left", py: 1.25, pr: 1, borderRadius: 2, font: "inherit",
                        "&.Mui-focusVisible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 } }}
                    >
                      <Box component="span" sx={(t) => ({
                        position: "relative", zIndex: 1, flexShrink: 0, width: 40, height: 40, borderRadius: "50%",
                        display: "grid", placeItems: "center", fontWeight: 800, fontSize: 15,
                        transition: "background-color 0.3s, color 0.3s, box-shadow 0.3s",
                        bgcolor: done || current ? "primary.main" : "background.paper",
                        color: done || current ? "primary.contrastText" : "text.secondary",
                        border: "2px solid", borderColor: done || current ? "primary.main" : "divider",
                        boxShadow: current ? `0 0 0 6px ${alpha(t.palette.primary.main, 0.18)}` : "none",
                      })}>
                        {done ? <CheckIcon aria-hidden="true" sx={{ fontSize: 20 }} /> : i + 1}
                      </Box>
                      <Box component="span" sx={{ display: "block", pt: 0.25 }}>
                        <Typography component="span" sx={{ display: "block", fontSize: 16.5, fontWeight: 700, transition: "color 0.3s", color: done || current ? "text.primary" : "text.secondary" }}>
                          {s.title}
                        </Typography>
                        <Typography component="span" sx={{ display: "block", mt: 0.25, fontSize: 14, lineHeight: 1.55, color: "text.secondary" }}>
                          {s.text}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>

            {/* The customer's phone, photographed, with the portal on its screen.
                The wallet card is the OPERATOR's side of the same sale, so it
                sits outside the phone, on the desk beside it — never on the
                customer's screen, where it would read as something the customer
                can see. */}
            <Box aria-hidden sx={{ position: "relative", minWidth: 0 }}>
              {/* 430, not "as big as it fits": the sticky story's content has to clear
                  the header inside STORY_MQ's floor, and this phone is the tallest
                  thing in it. At 430 the screen is 196x419 and every payment
                  screen renders with no overflow — measured, along with the
                  762px total that sets the floor in landingMotion. */}
              <PhoneStage maxWidth={430}>{screens}</PhoneStage>
              <Box
                data-wallet=""
               
                sx={{ position: "absolute", left: 0, bottom: "2%", width: "46%", minWidth: 190 }}
              >
                <WalletEntryCard />
              </Box>
              <Typography sx={{ mt: 1, textAlign: "center", fontSize: 11.5, color: "text.disabled" }}>
                Illustrative sample purchase
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
